import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import {
  child,
  get,
  onValue,
  ref,
  remove,
  set,
  update,
} from "firebase/database";
import { auth, db } from "../config/firebase.js";
import { TERM_WORDS } from "../data/termWords.js";
import {
  ROOM_PLAYER_LIMIT,
  buildLobbyCleanup,
  buildPuzzleRound,
  buildStopRound,
  buildStopScoreUpdates,
  calculateStopResults,
  createRoomState,
  generateRoomCode,
} from "../domain/room.js";

const RoomContext = createContext(null);
const ROOM_KEY = "@PartyGames:roomCode";
const USERNAME_KEY = "@PartyGames:username";
const waitForAuthRules = () =>
  new Promise((resolve) => setTimeout(resolve, 800));

async function retryPermissionDenied(operation) {
  try {
    return await operation();
  } catch (error) {
    if (error?.code !== "PERMISSION_DENIED") throw error;
    await waitForAuthRules();
    return operation();
  }
}

export function RoomProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roomCode, setRoomCode] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);

  const clearSession = () => {
    localStorage.removeItem(ROOM_KEY);
    localStorage.removeItem(USERNAME_KEY);
    setRoomCode(null);
    setRoomData(null);
    setIsHost(false);
  };

  useEffect(
    () =>
      onValue(ref(db, ".info/serverTimeOffset"), (snapshot) => {
        setServerTimeOffset(snapshot.val() || 0);
      }),
    [],
  );

  useEffect(
    () =>
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const savedCode = localStorage.getItem(ROOM_KEY);
          const savedUsername = localStorage.getItem(USERNAME_KEY);
          if (savedCode && savedUsername) {
            try {
              const roomRef = ref(db, `rooms/${savedCode}`);
              const snapshot = await get(roomRef);
              if (snapshot.exists() && snapshot.val().players) {
                const recoveredRoom = snapshot.val();
                let player = recoveredRoom.players[firebaseUser.uid];
                let host = recoveredRoom.hostId === firebaseUser.uid;

                if (!player) {
                  player = Object.values(recoveredRoom.players).find(
                    (candidate) =>
                      candidate.username.trim().toLowerCase() ===
                      savedUsername.trim().toLowerCase(),
                  );
                  if (player) {
                    const previousUid = player.id;
                    const nextUid = firebaseUser.uid;
                    const updates = {
                      [`players/${previousUid}`]: null,
                      [`players/${nextUid}`]: { ...player, id: nextUid },
                    };
                    if (recoveredRoom.hostId === previousUid) {
                      updates.hostId = nextUid;
                      host = true;
                    }
                    await update(roomRef, updates);
                    player = { ...player, id: nextUid };
                  }
                }

                if (player) {
                  setUser({ uid: firebaseUser.uid, username: player.username });
                  setRoomCode(savedCode);
                  setIsHost(host);
                  setLoading(false);
                  return;
                }
              }
            } catch (sessionError) {
              console.error("Erro ao recuperar sessão:", sessionError);
            }
            clearSession();
          }
        }
        setLoading(false);
      }),
    [],
  );

  useEffect(() => {
    if (!roomCode || !user?.uid) return undefined;
    return onValue(ref(db, `rooms/${roomCode}`), (snapshot) => {
      if (!snapshot.exists()) {
        clearSession();
        return;
      }
      const nextRoom = snapshot.val();
      if (nextRoom.players && !nextRoom.players[user.uid]) {
        clearSession();
        setError("Você foi removido da sala.");
        return;
      }
      setRoomData(nextRoom);
      setIsHost(nextRoom.hostId === user.uid);
    });
  }, [roomCode, user?.uid]);

  const ensureUser = async () =>
    auth.currentUser || (await signInAnonymously(auth)).user;

  const createRoom = async (rawUsername) => {
    setLoading(true);
    setError(null);
    const username = rawUsername.trim();
    try {
      const firebaseUser = await ensureUser();
      await firebaseUser.getIdToken(true);
      const code = generateRoomCode();
      const state = createRoomState({
        code,
        uid: firebaseUser.uid,
        username,
      });
      await retryPermissionDenied(() => set(ref(db, `rooms/${code}`), state));
      setUser({ uid: firebaseUser.uid, username });
      setRoomCode(code);
      setIsHost(true);
      localStorage.setItem(ROOM_KEY, code);
      localStorage.setItem(USERNAME_KEY, username);
    } catch (createError) {
      console.error(createError);
      setError("Erro ao criar a sala.");
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async (rawCode, rawUsername) => {
    setLoading(true);
    setError(null);
    const code = rawCode.trim().toUpperCase();
    const username = rawUsername.trim();
    try {
      const firebaseUser = await ensureUser();
      await firebaseUser.getIdToken(true);
      const snapshot = await retryPermissionDenied(() =>
        get(child(ref(db), `rooms/${code}`)),
      );

      if (!snapshot.exists()) {
        setError("Sala não encontrada. Verifique o código.");
        return;
      }
      const nextRoom = snapshot.val();
      if (nextRoom.status !== "lobby") {
        setError("A partida já começou! Não é possível entrar agora.");
        return;
      }
      const players = Object.values(nextRoom.players || {});
      if (
        players.some(
          (player) =>
            player.username.trim().toLowerCase() === username.toLowerCase(),
        )
      ) {
        setError("Esse apelido já está em uso nesta sala! Escolha outro.");
        return;
      }
      if (players.length >= ROOM_PLAYER_LIMIT) {
        setError(
          `A sala já está cheia (limite máximo de ${ROOM_PLAYER_LIMIT} jogadores).`,
        );
        return;
      }

      const player = {
        id: firebaseUser.uid,
        username,
        points: 0,
        isHost: false,
        pontosPorJogo: {},
      };
      await retryPermissionDenied(() =>
        set(ref(db, `rooms/${code}/players/${firebaseUser.uid}`), player),
      );
      setUser({ uid: firebaseUser.uid, username });
      setRoomCode(code);
      setIsHost(false);
      localStorage.setItem(ROOM_KEY, code);
      localStorage.setItem(USERNAME_KEY, username);
    } catch (joinError) {
      console.error(joinError);
      setError("Erro ao entrar.");
    } finally {
      setLoading(false);
    }
  };

  const leaveRoom = async () => {
    if (!roomCode || !user) return;
    try {
      await remove(
        ref(
          db,
          isHost
            ? `rooms/${roomCode}`
            : `rooms/${roomCode}/players/${user.uid}`,
        ),
      );
    } catch (leaveError) {
      console.error("Erro ao sair:", leaveError);
    } finally {
      clearSession();
    }
  };

  const hostSet = async (path, value) => {
    if (!roomCode || !isHost) return;
    await set(ref(db, `rooms/${roomCode}/${path}`), value);
  };

  const roomUpdate = async (values) => {
    if (!roomCode) return;
    await update(ref(db, `rooms/${roomCode}`), values);
  };

  const finishGame = async (gameId) => {
    if (!roomCode || !isHost || !roomData) return;
    await roomUpdate(buildLobbyCleanup(roomData, gameId));
  };

  const startGame = async () => {
    if (!roomCode || !isHost || !roomData?.gameId) return;
    const now = Date.now() + serverTimeOffset;
    const playerIds = Object.keys(roomData.players || {});

    if (roomData.gameId === "termo") {
      const wordCount = Number(roomData.config?.modoTermo) || 1;
      const values = {
        status: "jogando_termo",
        rodadaAtual: 1,
        termoPalavras: [...TERM_WORDS]
          .sort(() => 0.5 - Math.random())
          .slice(0, wordCount),
        timestampInicioRodada: now,
      };
      playerIds.forEach((uid) => {
        values[`players/${uid}/termoStatus`] = null;
      });
      await roomUpdate(values);
    } else if (roomData.gameId === "quebra-cabeca") {
      await roomUpdate(buildPuzzleRound(roomData, 1, now));
    } else if (roomData.gameId === "stop") {
      await roomUpdate({
        ...buildStopRound(roomData, 1, now),
        stopLetrasUsadas: [],
      });
    } else {
      await roomUpdate({
        status: "escolhendo_personagem",
        rodadaAtual: 1,
        turnoAtual: 0,
        ordemJogadores: playerIds,
        chooserId: playerIds[0],
      });
    }
  };

  const nextRound = async () => {
    if (!roomCode || !isHost || !roomData) return;
    const gameId = roomData.gameId || "quem-sou-eu";
    const maxRounds = Number(roomData.config?.maxRodadas) || 3;
    let round = Number(roomData.rodadaAtual) || 1;
    const now = Date.now() + serverTimeOffset;

    if (["termo", "quebra-cabeca", "stop"].includes(gameId)) {
      round += 1;
      if (round > maxRounds) return finishGame(gameId);
      if (gameId === "termo") {
        const wordCount = Number(roomData.config?.modoTermo) || 1;
        const values = {
          status: "jogando_termo",
          rodadaAtual: round,
          termoPalavras: [...TERM_WORDS]
            .sort(() => 0.5 - Math.random())
            .slice(0, wordCount),
          timestampInicioRodada: now,
        };
        Object.keys(roomData.players || {}).forEach((uid) => {
          values[`players/${uid}/termoStatus`] = null;
        });
        return roomUpdate(values);
      }
      if (gameId === "quebra-cabeca") {
        return roomUpdate(buildPuzzleRound(roomData, round, now));
      }
      return roomUpdate(buildStopRound(roomData, round, now));
    }

    const order =
      roomData.ordemJogadores || Object.keys(roomData.players || {});
    let turn = Number(roomData.turnoAtual) || 0;
    turn += 1;
    if (turn >= order.length) {
      turn = 0;
      round += 1;
    }
    if (round > maxRounds) return finishGame("quem-sou-eu");

    const values = {
      status: "escolhendo_personagem",
      rodadaAtual: round,
      turnoAtual: turn,
      chooserId: order[turn],
      personagemNome: null,
      personagemImagem: null,
      personagemAliases: null,
      resultadoUltimaRodada: null,
    };
    Object.keys(roomData.players || {}).forEach((uid) => {
      values[`players/${uid}/quemSouEuStatus`] = null;
    });
    await roomUpdate(values);
  };

  const value = useMemo(
    () => ({
      user,
      roomCode,
      roomData,
      isHost,
      loading,
      error,
      serverTimeOffset,
      createRoom,
      joinRoom,
      updateRoomConfig: async (key, rawValue) => {
        const value =
          typeof rawValue === "string" ? rawValue : Number(rawValue);
        await hostSet(`config/${key}`, value);
      },
      selecionarJogo: (gameId) => hostSet("gameId", gameId),
      trocarJogo: () => hostSet("gameId", null),
      awardPoints: async (uid, points, extra = {}) => {
        if (!roomCode || !roomData?.gameId || !roomData.players?.[uid]) return;
        const player = roomData.players[uid];
        const gameId = roomData.gameId;
        await roomUpdate({
          [`players/${uid}/points`]: (player.points || 0) + points,
          [`players/${uid}/pontosPorJogo/${gameId}`]:
            (player.pontosPorJogo?.[gameId] || 0) + points,
          ...extra,
        });
      },
      startGame,
      nextRound,
      endGame: async () => {
        if (!isHost || !roomData) return;
        const values = { status: "ranking_final" };
        if (roomData.gameId) values[`jogosJogados/${roomData.gameId}`] = true;
        await roomUpdate(values);
      },
      resetGame: async () => {
        if (!isHost || !roomData) return;
        const values = {};
        Object.keys(roomData.players || {}).forEach((uid) => {
          values[`players/${uid}/points`] = 0;
          values[`players/${uid}/pontosPorJogo`] = null;
          values[`players/${uid}/termoStatus`] = null;
          values[`players/${uid}/quebraCabecaStatus`] = null;
          values[`players/${uid}/quemSouEuStatus`] = null;
        });
        await roomUpdate({
          status: "lobby",
          gameId: null,
          jogosJogados: null,
          rodadaAtual: null,
          turnoAtual: null,
          ordemJogadores: null,
          chooserId: null,
          resultadoUltimaRodada: null,
          personagemNome: null,
          personagemImagem: null,
          personagemAliases: null,
          timestampInicioRodada: null,
          personagensUsados: null,
          termoPalavras: null,
          imagemQuebraCabeca: null,
          stopLetraAtual: null,
          stopLetrasUsadas: null,
          stopParadoPor: null,
          stopRespostas: null,
          stopVotos: null,
          stopResultadoRodada: null,
          ...values,
        });
      },
      trocarJogoDuranteJogo: () =>
        roomData?.gameId ? finishGame(roomData.gameId) : undefined,
      escolherPersonagemQuemSouEu: async ({ name, imageUrl, aliases }) => {
        if (!roomData?.players || !user?.uid) return;
        const order = roomData.ordemJogadores || Object.keys(roomData.players);
        const chooserId = order[Number(roomData.turnoAtual) || 0];
        if (chooserId !== user.uid) return;
        const values = {
          status: "sorteando_jogador",
          personagemNome: name,
          personagemImagem: imageUrl,
          personagemAliases: aliases?.length ? aliases : null,
        };
        Object.keys(roomData.players).forEach((uid) => {
          if (uid !== chooserId) {
            values[`players/${uid}/quemSouEuStatus`] = null;
          }
        });
        await roomUpdate(values);
      },
      iniciarRodadaQuemSouEu: async () => {
        if (!isHost || roomData?.status !== "sorteando_jogador") return;
        await roomUpdate({
          status: "rodada_ativa",
          timestampInicioRodada: Date.now() + serverTimeOffset + 4000,
        });
      },
      finalizarRodadaQuemSouEu: async () => {
        if (!isHost || !roomData?.players) return;
        const order = roomData.ordemJogadores || Object.keys(roomData.players);
        const chooserId = order[Number(roomData.turnoAtual) || 0];
        const values = { status: "fim_rodada" };
        Object.values(roomData.players).forEach((player) => {
          if (player.id !== chooserId && !player.quemSouEuStatus?.finalizado) {
            values[`players/${player.id}/quemSouEuStatus`] = {
              finalizado: true,
              acertou: false,
              colocacao: null,
              pontosGanhos: 0,
            };
          }
        });
        await roomUpdate(values);
      },
      escolherImagemQuebraCabeca: async (imageUrl) => {
        if (!isHost || roomData?.status !== "escolhendo_imagem_quebra_cabeca")
          return;
        await roomUpdate({
          status: "jogando_quebra_cabeca",
          imagemQuebraCabeca: imageUrl,
          timestampInicioRodada: Date.now() + serverTimeOffset,
        });
      },
      salvarRespostasStop: async (uid, answers) => {
        if (!roomCode || !uid) return;
        await set(ref(db, `rooms/${roomCode}/stopRespostas/${uid}`), answers);
      },
      pararStop: async (uid = null) => {
        if (roomData?.status !== "jogando_stop") return;
        await roomUpdate({ status: "votacao_stop", stopParadoPor: uid });
      },
      votarRespostaStop: async (targetUid, categoryId, vote) => {
        if (!roomCode || !user?.uid || targetUid === user.uid) return;
        await set(
          ref(
            db,
            `rooms/${roomCode}/stopVotos/${targetUid}/${categoryId}/${user.uid}`,
          ),
          vote,
        );
      },
      apurarResultadoStop: async () => {
        if (!isHost || roomData?.gameId !== "stop") return;
        const results = calculateStopResults(roomData);
        await roomUpdate(buildStopScoreUpdates(roomData, results));
      },
      leaveRoom,
      kickPlayer: async (uid) => {
        if (!roomCode || !isHost) return;
        await remove(ref(db, `rooms/${roomCode}/players/${uid}`));
      },
    }),
    [user, roomCode, roomData, isHost, loading, error, serverTimeOffset],
  );

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export function useRoom() {
  const context = useContext(RoomContext);
  if (!context)
    throw new Error("useRoom deve ser usado dentro de RoomProvider");
  return context;
}
